import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowDown, ArrowUp, Lock, Trash2 } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { IntakeGroup, IntakeKind, IntakeSection, IntakeSectionBody, IntakeSectionsView, IntakeTab } from '../../types/aai';
import { Action, Chip, ErrorNote, Field, Input, Loading, Modal, Select, Tabs } from '../../components/aa';

/*
 * The client intake in five tabs: Opening, Business, Department, Person and
 * Tool. Agents Anywhere's sections are the defaults every client starts from;
 * the super admin edits them here. The opening questions are the same for
 * every client. A Solution Partner makes their own version of our other
 * sections for their clients (delete, add, reorder, reword) and adds
 * sections of their own, choosing who at the client receives each.
 */

type DraftQuestion = { id?: string; label: string; kind: IntakeKind; options: string; optional: boolean };
type Draft = {
  id?: string;
  /** Editing a partner's version of one of our sections: only the questions change. */
  ours?: boolean;
  intake: IntakeTab;
  title: string;
  description: string;
  groups: IntakeGroup[];
  perTool: boolean;
  questions: DraftQuestion[];
};

const TABS: { id: IntakeTab; label: string; about: string; groups: IntakeGroup[] }[] = [
  { id: 'opening', label: 'Opening questions', about: 'Asked right after onboarding, of everyone. Nobody can skip them, and they are the same for every client.', groups: ['owners', 'heads', 'employees'] },
  { id: 'business', label: 'Business intake', about: 'About the company. Goes to the Owner and AI Champion.', groups: ['owners'] },
  { id: 'department', label: 'Department intake', about: 'About one department. Goes to department heads, about their own department.', groups: ['heads'] },
  { id: 'person', label: 'Person intake', about: 'About the person themselves. Goes to everyone.', groups: ['owners', 'heads', 'employees'] },
  { id: 'tool', label: 'Tool intake', about: 'About the tools people use. Goes to the Owner and employees, once for each tool they use.', groups: ['owners', 'employees'] },
];

const GROUPS: { id: IntakeGroup; label: string }[] = [
  { id: 'owners', label: 'Owner and AI Champion' },
  { id: 'heads', label: 'Department heads' },
  { id: 'employees', label: 'Employees' },
];

const KIND_LABEL: Record<IntakeKind, string> = {
  text: 'Written answer',
  one: 'Pick one',
  many: 'Pick many',
  number: 'Number',
};

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

const groupsOf = (audience: string | undefined): IntakeGroup[] => {
  if (!audience || audience === 'everyone') return GROUPS.map((g) => g.id);
  return audience.split(',').filter((g): g is IntakeGroup => GROUPS.some((x) => x.id === g));
};

const audienceLabel = (audience: string | undefined): string => {
  const groups = groupsOf(audience);
  if (groups.length === GROUPS.length) return 'Everyone';
  return GROUPS.filter((g) => groups.includes(g.id)).map((g) => g.label).join(', ');
};

const tabOf = (s: IntakeSection): IntakeTab => s.intake || 'person';

const questionCount = (n: number): string => {
  if (n === 1) return '1 question';
  return `${n} questions`;
};

const kindLine = (q: { kind?: IntakeKind; options?: string[] }): string => {
  const kind = q.kind || 'text';
  if (kind === 'one' || kind === 'many') return `${KIND_LABEL[kind]} · ${(q.options || []).length}`;
  return KIND_LABEL[kind];
};

const metaLine = (s: IntakeSection): string => {
  const parts = [questionCount(s.questions.length), audienceLabel(s.audience)];
  if (s.repeat === 'tool') parts.push('once per tool');
  if (s.repeat === 'department') parts.push('about their own department');
  if (tabOf(s) === 'opening') parts.push('must be answered');
  return parts.join(' · ');
};

const emptyQuestion = (): DraftQuestion => ({ label: '', kind: 'text', options: '', optional: false });

const emptyDraft = (intake: IntakeTab): Draft => {
  const tab = TABS.find((t) => t.id === intake) || TABS[0];
  return { intake, title: '', description: '', groups: tab.groups, perTool: intake === 'tool', questions: [emptyQuestion()] };
};

const draftOf = (s: IntakeSection, ours = false): Draft => ({
  id: s.id,
  ours,
  intake: tabOf(s),
  title: s.title,
  description: s.description,
  groups: groupsOf(s.audience),
  perTool: s.repeat === 'tool',
  questions: s.questions.map((q) => ({
    id: q.id, label: q.label, kind: q.kind || 'text', options: (q.options || []).join('\n'), optional: Boolean(q.optional),
  })),
});

const bodyQuestions = (d: Draft): IntakeSectionBody['questions'] => d.questions.filter((q) => q.label.trim()).map((q) => ({
  id: q.id,
  label: q.label,
  kind: q.kind,
  options: q.options.split('\n').map((o) => o.trim()).filter(Boolean),
  optional: q.optional,
}));

const IconButton: React.FC<{ label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ label, disabled = false, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-ink-dim transition-colors hover:bg-canvas hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
  >
    {children}
  </button>
);

type RowActions = {
  onUp?: () => void;
  onDown?: () => void;
  onEdit?: () => void;
  editLabel?: string;
  onDelete?: () => void;
  deleteLabel?: string;
  onRestore?: () => void;
  onReset?: () => void;
};

const SectionCard: React.FC<{ section: IntakeSection; chips: React.ReactNode; busy: boolean } & RowActions> = ({
  section, chips, busy, onUp, onDown, onEdit, editLabel = 'Edit', onDelete, deleteLabel = 'Delete', onRestore, onReset,
}) => {
  let frame = 'overflow-hidden rounded-[12px] border border-line bg-white';
  if (section.removed) frame += ' opacity-60';
  return (
    <div className={frame}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-soft px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-[14.5px] text-ink" title={section.description}>{section.title}</strong>
            {chips}
          </div>
          <p className="m-0 text-[12px] text-ink-dim">{metaLine(section)}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {(onUp || onDown) && (
            <>
              <IconButton label={`Move ${section.title} up`} disabled={!onUp || busy} onClick={() => onUp && onUp()}><ArrowUp size={13} /></IconButton>
              <IconButton label={`Move ${section.title} down`} disabled={!onDown || busy} onClick={() => onDown && onDown()}><ArrowDown size={13} /></IconButton>
            </>
          )}
          {onRestore && <Action size="sm" variant="outline" onClick={onRestore} disabled={busy}>Bring back</Action>}
          {onReset && <Action size="sm" variant="quiet" onClick={onReset} disabled={busy}>Reset to ours</Action>}
          {onEdit && <Action size="sm" variant="outline" onClick={onEdit} disabled={busy}>{editLabel}</Action>}
          {onDelete && <Action size="sm" variant="quiet" onClick={onDelete} disabled={busy}>{deleteLabel}</Action>}
        </div>
      </div>
      <ol className="m-0 list-none divide-y divide-line-soft p-0">
        {section.questions.map((q, i) => (
          <li key={q.id} className="flex items-baseline gap-3 px-4 py-1.5 text-[13px]">
            <span className="w-5 shrink-0 text-right tabular-nums text-ink-dim">{i + 1}</span>
            <span className="min-w-0 flex-1 text-ink">{q.label}</span>
            <span className="shrink-0 whitespace-nowrap text-[11.5px] text-ink-dim">
              {kindLine(q)}
              {q.optional && ' · optional'}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const QuestionEditor: React.FC<{
  q: DraftQuestion;
  index: number;
  count: number;
  opening: boolean;
  onChange: (next: DraftQuestion) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}> = ({ q, index, count, opening, onChange, onMove, onRemove }) => {
  const choices = q.kind === 'one' || q.kind === 'many';
  return (
    <div className="rounded-[10px] border border-line-soft bg-canvas/40 px-2 py-2">
      <div className="flex items-center gap-1.5">
        <div className="flex flex-col">
          <button type="button" aria-label={`Move question ${index + 1} up`} disabled={index === 0} onClick={() => onMove(-1)}
            className="text-ink-dim hover:text-ink disabled:opacity-25"><ArrowUp size={12} /></button>
          <button type="button" aria-label={`Move question ${index + 1} down`} disabled={index === count - 1} onClick={() => onMove(1)}
            className="text-ink-dim hover:text-ink disabled:opacity-25"><ArrowDown size={12} /></button>
        </div>
        <span className="w-5 shrink-0 text-right text-[12.5px] tabular-nums text-ink-dim">{index + 1}.</span>
        <Input value={q.label} onChange={(e) => onChange({ ...q, label: e.target.value })} placeholder="Write a question" aria-label={`Question ${index + 1}`} />
        <Select
          value={q.kind}
          onChange={(e) => onChange({ ...q, kind: e.target.value as IntakeKind })}
          aria-label={`Answer type for question ${index + 1}`}
          className="!w-[140px] shrink-0"
        >
          {(Object.keys(KIND_LABEL) as IntakeKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </Select>
        <IconButton label={`Remove question ${index + 1}`} disabled={count === 1} onClick={onRemove}><Trash2 size={13} /></IconButton>
      </div>
      {(choices || !opening) && (
        <div className="mt-1.5 flex flex-wrap items-start gap-3 pl-[50px] pr-9">
          {choices && (
            <textarea
              value={q.options}
              onChange={(e) => onChange({ ...q, options: e.target.value })}
              rows={2}
              placeholder="One choice per line"
              aria-label={`Choices for question ${index + 1}`}
              className="min-w-0 flex-1 rounded-act border border-input bg-white px-2.5 py-1.5 text-[12.5px] text-ink outline-none focus:border-ink focus:ring-[3px] focus:ring-ink/10"
            />
          )}
          {!opening && (
            <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-ink-muted">
              <input type="checkbox" checked={q.optional} onChange={(e) => onChange({ ...q, optional: e.target.checked })} />
              Optional
            </label>
          )}
        </div>
      )}
    </div>
  );
};

const SectionEditor: React.FC<{ draft: Draft; saving: boolean; onClose: () => void; onSave: (d: Draft) => void }> = ({ draft, saving, onClose, onSave }) => {
  const [form, setForm] = useState<Draft>(draft);
  const opening = form.intake === 'opening';
  const setQuestion = (i: number, next: DraftQuestion) => setForm((f) => ({ ...f, questions: f.questions.map((q, n) => (n === i ? next : q)) }));
  const removeQuestion = (i: number) => setForm((f) => ({ ...f, questions: f.questions.filter((_, n) => n !== i) }));
  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, emptyQuestion()] }));
  const moveQuestion = (i: number, direction: -1 | 1) => setForm((f) => {
    const to = i + direction;
    if (to < 0 || to >= f.questions.length) return f;
    const next = [...f.questions];
    [next[i], next[to]] = [next[to], next[i]];
    return { ...f, questions: next };
  });
  const toggleGroup = (g: IntakeGroup) => setForm((f) => {
    if (f.groups.includes(g)) return { ...f, groups: f.groups.filter((x) => x !== g) };
    return { ...f, groups: [...f.groups, g] };
  });

  const tabLabel = (TABS.find((t) => t.id === form.intake) || TABS[0]).label;
  let heading = `Add section · ${tabLabel}`;
  if (form.id) heading = `Edit ${form.title}`;
  if (form.ours) heading = `Your version of ${form.title}`;
  let saveLabel = 'Save section';
  if (form.ours) saveLabel = 'Save for my clients';

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={heading}
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="ghost" onClick={onClose}>Cancel</Action>
          <Action onClick={() => onSave(form)} disabled={saving} reason="Saving">{saveLabel}</Action>
        </div>
      )}
    >
      <div className="space-y-4 p-1">
        {form.ours && (
          <p className="m-0 text-[12.5px] text-ink-muted">
            Delete, add, reorder or reword questions. The change reaches your clients only, and you can reset to ours at any time.
            Answers already given to a deleted question stay with Orbit.
          </p>
        )}
        {!form.ours && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Title" htmlFor="intake-section-title">
              <Input id="intake-section-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Goals" autoFocus />
            </Field>
            <Field label="Description" htmlFor="intake-section-description">
              <Input id="intake-section-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this helps us understand." />
            </Field>
          </div>
        )}
        {!form.ours && (
          <Field label="Who receives it?">
            <div className="flex flex-wrap gap-1.5">
              {GROUPS.map((g) => {
                const on = form.groups.includes(g.id);
                let cls = 'rounded-full border px-3 py-1 text-[12.5px] font-semibold transition-colors';
                if (on) cls += ' border-ink bg-ink text-white';
                if (!on) cls += ' border-line bg-white text-ink-muted hover:text-ink';
                return <button key={g.id} type="button" aria-pressed={on} onClick={() => toggleGroup(g.id)} className={cls}>{g.label}</button>;
              })}
            </div>
          </Field>
        )}
        {!form.ours && form.intake === 'tool' && (
          <label className="flex items-center gap-2 text-[12.5px] text-ink">
            <input type="checkbox" checked={form.perTool} onChange={(e) => setForm({ ...form, perTool: e.target.checked })} />
            Ask these once for each tool the person uses
          </label>
        )}
        {opening && <p className="m-0 text-[12.5px] text-ink-dim">Opening questions must be answered and are the same for every client.</p>}
        <Field label="Questions">
          <div className="space-y-1.5">
            {form.questions.map((q, i) => (
              <QuestionEditor
                key={q.id || `new-${i}`}
                q={q}
                index={i}
                count={form.questions.length}
                opening={opening}
                onChange={(next) => setQuestion(i, next)}
                onMove={(direction) => moveQuestion(i, direction)}
                onRemove={() => removeQuestion(i)}
              />
            ))}
            <Action size="sm" variant="quiet" onClick={addQuestion}>Add question</Action>
          </div>
        </Field>
      </div>
    </Modal>
  );
};

export const ClientIntake: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [view, setView] = useState<IntakeSectionsView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<IntakeTab>('opening');
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await aaiApi.intakeSections();
      setView(r.data);
    } catch (err) {
      setError(errorText(err, 'Could not load the client intake.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (work: () => Promise<{ data: IntakeSectionsView }>, done: string, failed: string): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await work();
      setView(r.data);
      if (done) toast.success(done);
      return true;
    } catch (err) {
      toast.error(errorText(err, failed));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (d: Draft) => {
    const questions = bodyQuestions(d);
    if (questions.length === 0) { toast.error('Add at least one question.'); return; }
    let ok = false;
    if (d.ours && d.id) {
      const id = d.id;
      ok = await run(() => aaiApi.adjustOurIntakeSection(id, { questions, removed: false }), 'Saved for your clients.', 'Could not save your version.');
      if (ok) setDraft(null);
      return;
    }
    if (d.groups.length === 0) { toast.error('Choose who receives the section.'); return; }
    let repeat = '';
    if (d.perTool) repeat = 'tool';
    const body = { intake: d.intake, title: d.title, description: d.description, audience: d.groups.join(','), repeat, questions };
    if (d.id) {
      const id = d.id;
      ok = await run(() => aaiApi.editIntakeSection(id, body), 'Section saved.', 'Could not save the section.');
    }
    if (!d.id) ok = await run(() => aaiApi.addIntakeSection(body), 'Section added.', 'Could not add the section.');
    if (ok) setDraft(null);
  };

  const removeOurs = (s: IntakeSection) => {
    if (!window.confirm(`Remove ${s.title} for your clients? You can bring it back at any time.`)) return;
    run(() => aaiApi.adjustOurIntakeSection(s.id, { questions: [], removed: true }), 'Removed for your clients.', 'Could not remove the section.');
  };
  const restoreOurs = (s: IntakeSection) => {
    run(() => aaiApi.adjustOurIntakeSection(s.id, { questions: [], removed: false }), 'Brought back for your clients.', 'Could not bring the section back.');
  };
  const resetOurs = (s: IntakeSection) => {
    if (!window.confirm(`Go back to our version of ${s.title}? Your changes to its questions are dropped.`)) return;
    run(() => aaiApi.resetOurIntakeSection(s.id), 'Back to our version.', 'Could not reset the section.');
  };
  const remove = (s: IntakeSection) => {
    if (!window.confirm(`Delete ${s.title}? Answers already given to it are deleted too.`)) return;
    run(() => aaiApi.deleteIntakeSection(s.id), 'Section deleted.', 'Could not delete the section.');
  };
  const move = (s: IntakeSection, direction: 'up' | 'down') => {
    run(() => aaiApi.moveIntakeSection(s.id, direction), '', 'Could not move the section.');
  };

  if (error) return <ErrorNote message={error} />;
  if (!view) return <Loading label="Reading the client intake" />;

  const platform = !view.scope;
  const inTab = (s: IntakeSection) => tabOf(s) === tab;
  const ours = view.fixed.filter(inTab);
  const own = view.sections.filter(inTab);
  const current = TABS.find((t) => t.id === tab) || TABS[0];
  const tabItems = TABS.map((t) => ({
    id: t.id,
    label: t.label,
    count: [...view.fixed, ...view.sections].filter((s) => tabOf(s) === t.id && !s.removed).reduce((n, s) => n + s.questions.length, 0),
  }));
  let clientsLine = `Live for ${view.clients} clients`;
  if (view.clients === 1) clientsLine = 'Live for 1 client';

  const ordered = (list: IntakeSection[], i: number, s: IntakeSection) => {
    const actions: RowActions = {};
    if (list.length < 2) return actions;
    if (i > 0) actions.onUp = () => move(s, 'up');
    if (i < list.length - 1) actions.onDown = () => move(s, 'down');
    return actions;
  };

  const oursCard = (s: IntakeSection, i: number) => {
    if (platform) {
      let chip = <Chip tone="ok">Partners can edit</Chip>;
      if (tabOf(s) === 'opening') chip = <Chip tone="neutral"><span className="inline-flex items-center gap-1"><Lock size={10} />Fixed for every client</span></Chip>;
      return <SectionCard key={s.id} section={s} chips={chip} busy={busy} {...ordered(ours, i, s)} onEdit={() => setDraft(draftOf(s))} onDelete={() => remove(s)} />;
    }
    if (tabOf(s) === 'opening') {
      return <SectionCard key={s.id} section={s} busy={busy} chips={<Chip tone="neutral"><span className="inline-flex items-center gap-1"><Lock size={10} />Fixed by Agents Anywhere</span></Chip>} />;
    }
    if (s.removed) {
      return <SectionCard key={s.id} section={s} busy={busy} chips={<Chip tone="neutral">Removed for your clients</Chip>} onRestore={() => restoreOurs(s)} />;
    }
    let chip = <Chip tone="neutral">From Agents Anywhere</Chip>;
    if (s.customised) chip = <Chip tone="run">Your version</Chip>;
    let reset: (() => void) | undefined;
    if (s.customised) reset = () => resetOurs(s);
    return (
      <SectionCard
        key={s.id}
        section={s}
        busy={busy}
        chips={chip}
        onReset={reset}
        onEdit={() => setDraft(draftOf(s, true))}
        editLabel="Edit questions"
        onDelete={() => removeOurs(s)}
        deleteLabel="Remove"
      />
    );
  };

  let eyebrow = 'Champion Framework';
  let title = 'Client intake';
  if (platform) { eyebrow = 'Platform'; title = 'Intake Studio'; }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          {!embedded && <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">{eyebrow}</p>}
          {!embedded && <h1 className="m-0 text-[24px] font-bold leading-tight text-ink">{title}</h1>}
          {platform && !embedded && <p className="m-0 mt-1 text-[13.5px] text-ink-muted">The questions every client starts with. Partners keep the opening questions and can change the rest for their own clients.</p>}
          {!platform && <p className="m-0 mt-1 text-[13.5px] text-ink-muted">{clientsLine}. The opening questions are fixed; change the rest for your clients, or add your own sections.</p>}
        </div>
        {(platform || tab !== 'opening') && <Action size="sm" onClick={() => setDraft(emptyDraft(tab))}>Add section</Action>}
      </div>

      <Tabs tabs={tabItems} active={tab} onChange={(id) => setTab(id as IntakeTab)} label="Intakes" className="!mb-2" />
      <p className="m-0 mb-3 text-[12.5px] text-ink-dim">{current.about} Orbit fills in what it already knows; people confirm it.</p>

      <div className="space-y-3">
        {ours.map((s, i) => oursCard(s, i))}
        {own.map((s, i) => (
          <SectionCard
            key={s.id}
            section={s}
            busy={busy}
            chips={<Chip tone="ok">Your section</Chip>}
            {...ordered(own, i, s)}
            onEdit={() => setDraft(draftOf(s))}
            onDelete={() => remove(s)}
          />
        ))}
        {ours.length === 0 && own.length === 0 && (
          <div className="rounded-[12px] border border-dashed border-line px-4 py-5 text-center text-[13px] text-ink-dim">
            No sections in {current.label} yet.
          </div>
        )}
      </div>

      {draft && <SectionEditor draft={draft} saving={busy} onClose={() => setDraft(null)} onSave={save} />}
    </div>
  );
};

export default ClientIntake;
